import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLanguageStore } from '../stores/languageStore';
import { translate } from '../utils/translate';

interface TranslationContextType {
  translateText: (text: string, from?: string, to?: string) => Promise<string>;
  isTranslating: boolean;
  currentLanguage: string;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

const translationCache: Record<string, { result: string; timestamp: number }> = {};
const CACHE_EXPIRY_MS = 24 * 60 * 60 * 1000;

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { language } = useLanguageStore();
  const [isTranslating, setIsTranslating] = useState(false);

  const translateText = useCallback(async (text: string, from: string = 'zh', to: string = language) => {
    if (!text.trim()) return text;
    if (from === to) return text;

    const cacheKey = `${text}:${from}:${to}`;
    
    if (translationCache[cacheKey] && Date.now() - translationCache[cacheKey].timestamp < CACHE_EXPIRY_MS) {
      return translationCache[cacheKey].result;
    }

    setIsTranslating(true);
    try {
      const result = await translate(text, from, to);
      translationCache[cacheKey] = {
        result,
        timestamp: Date.now()
      };
      return result;
    } catch (error) {
      console.error('翻译失败:', error);
      return text;
    } finally {
      setIsTranslating(false);
    }
  }, [language]);

  return (
    <TranslationContext.Provider value={{ translateText, isTranslating, currentLanguage: language }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslation = (): TranslationContextType => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};